import { STORAGE_LANGUAGE_NAME } from "constants/variables";
import { useMemo } from "react";
import { useSelector } from "react-redux";

// Selectors
import * as userSelectors from "redux/user/selectors";
import { useCatalogsStore } from "stores/catalogs";

// Types
import { CurrencySignPositionEnum, CurrencyType } from "types/Currency";
import {
  formatFractionalAndThousand,
  getSeparatorsForLanguage,
  splitAmountString,
} from "utils/formatters";

const useFormatBalance = (overrideCurrencyCode?: string) => {
  const userCurrency = useSelector(userSelectors.currency);
  const userLanguage = (
    localStorage.getItem(STORAGE_LANGUAGE_NAME) || "en"
  ).toLowerCase();
  const currencies = useCatalogsStore((s) => s.currencies);

  const currencyMap = useMemo(() => {
    if (!currencies) {
      return null;
    }
    return currencies.reduce<
      Record<string, Pick<CurrencyType, "sign" | "signPosition">>
    >((acc, c) => {
      const normalizedPos =
        (CurrencySignPositionEnum[
          c.signPosition as keyof typeof CurrencySignPositionEnum
        ] as CurrencySignPositionEnum) ?? CurrencySignPositionEnum.AFTER;

      // eslint-disable-next-line no-param-reassign
      acc[c.isoCode.toUpperCase()] = {
        sign: c.sign,
        signPosition: normalizedPos as any,
      };
      return acc;
    }, {});
  }, [currencies]);

  const currencyMeta = useMemo<Pick<
    CurrencyType,
    "sign" | "signPosition"
  > | null>(() => {
    const oc = overrideCurrencyCode?.toUpperCase();
    if (oc && currencyMap?.[oc]) {
      return currencyMap[oc];
    }

    if (userCurrency) {
      const normalizedPos =
        (CurrencySignPositionEnum[
          userCurrency.signPosition as keyof typeof CurrencySignPositionEnum
        ] as CurrencySignPositionEnum) ?? CurrencySignPositionEnum.AFTER;

      return { sign: userCurrency.sign, signPosition: normalizedPos as any };
    }
    return null;
  }, [overrideCurrencyCode, currencyMap, userCurrency]);

  return (number: number) => {
    if (number === undefined || number === null) {
      return "0";
    }

    const raw = formatFractionalAndThousand(number, userLanguage);
    const formatted = splitAmountString(
      raw,
      getSeparatorsForLanguage(userLanguage).fractionalSeparator,
    );

    if (currencyMeta) {
      return currencyMeta.signPosition === CurrencySignPositionEnum.AFTER
        ? `${formatted} ${currencyMeta.sign}`
        : `${currencyMeta.sign} ${formatted}`;
    }
    return formatted;
  };
};

export default useFormatBalance;
